import { useContext, useEffect, useRef, useState } from "react";
import { SettingsContext, CartContext, TableContext, AlertContext, ShopContext, UserContext } from "../App";
import currencyFormat, { getVisibleCartView } from "./functions/functions";
import Api from "./api/api";
import "../css/Cart.css";
import PaymentPopup from "./PaymentPopup";
import YellgoPayPopup from "./YellgoPayPopup";
import CloseButton from "./CloseButton";
import Products from "./cart/Products";
import Label from "./Label";
import NotesPopup from "./cart/NotesPopup";
import ProductNotesPopup from "./cart/ProductNotesPopup";

function Cart({ setCartVisible }) {
  const {
    settings,
    legal,
    setLegalPupop,
    setPagePopup,
    accessType,
    credit,
    setCredit,
    userID,
    getRelatedProducts,
    timerActive,
    monoOrder,
    handleCheckout,
    showPaymentPopup,
    setShowPaymentPopup,
    showYellgoPayPopup,
    checkout,
    closeCart,
    marketing,
    setMarketing,
    newsletter,
    setNewsletter,
    setLoading,
    singleOrderMode,
    productNotesPopup,
    setProductNotesPopup,
    additionalCheckoutFields,
    editCart,
    editTable,
    lang,
  } = useContext(SettingsContext);
  const { shop, brand } = useContext(ShopContext);
  const { table } = useContext(TableContext);
  const { cart, updateQty } = useContext(CartContext);
  const visibleCart = getVisibleCartView(cart, settings);
  const { setAlertData } = useContext(AlertContext);
  const { user } = useContext(UserContext);
  const [notesPopup, setNotesPopup] = useState(false);
  const [additionalFieldsValues, setAdditionalFieldsValues] = useState({});
  const lastSavedAdditionalFieldsRef = useRef({});
  const [additionalFieldsErrors, setAdditionalFieldsErrors] = useState({});

  useEffect(() => {
    if (settings.menu_api?.enable_related_products_on_checkout == 1) {
      getRelatedProducts(getCartProductsIds());
    }
  }, []);

  useEffect(() => {
    const source = cart?.additional_checkout_fields;
    if (Array.isArray(source)) {
      const nextValues = {};
      for (const field of source) {
        if (field && field.id !== undefined) nextValues[field.id] = field.value;
      }
      setAdditionalFieldsValues(nextValues);
      lastSavedAdditionalFieldsRef.current = nextValues;
    }
  }, [cart?.additional_checkout_fields]);

  const countProducts = () => {
    var count = 0;
    for (var i = 0; i < cart.products.length; i++) count += cart.products[i].qty;
    return count;
  };

  const addDiscountFidelity = async (e) => {
    setLoading(true);
    const response = await Api.post("/self_cart/add_discount_fidelity/" + table.id + "/", {
      mono_order: monoOrder,
      discount_id: e.target.value,
    });
    setLoading(false);
    if (response.success == 0)
      setAlertData({
        message: response.error,
        confirmMessage: <Label number={31} />,
      });
  };

  const deleteDiscountFidelity = async () => {
    setLoading(true);
    const response = await Api.post("/self_cart/delete_discount_fidelity/" + table.id + "/", {
      mono_order: monoOrder,
    });
    setLoading(false);
    if (response.success == 0)
      setAlertData({
        message: response.error,
        confirmMessage: <Label number={31} />,
      });
  };

  const addDiscountCode = async () => {
    setLoading(true);
    const response = await Api.post("/self_cart/add_discount_code/" + table.id + "/", {
      mono_order: monoOrder,
      shop_id: shop.id,
      code: document.getElementById("code").value,
      customer_id: user.id,
    });
    setLoading(false);
    if (response.success === 0)
      setAlertData({
        message: response.error,
        confirmMessage: <Label number={31} />,
      });
  };

  const deleteDiscountCode = async (item_id) => {
    setLoading(true);
    const response = await Api.post("/self_cart/delete_discount_code/" + table.id + "/", {
      item_id: item_id,
    });
    setLoading(false);
    if (response.success === 0)
      setAlertData({
        message: response.error,
        confirmMessage: <Label number={31} />,
      });
  };

  const saveNotes = async (request) => {
    console.log("saveNotes");
    request.mono_order = monoOrder;
    setLoading(true);
    const response = await Api.post("/self_cart/save_notes/" + table.id + "/", request);
    setLoading(false);
    if (response.success === 0)
      setAlertData({
        message: response.error,
        confirmMessage: <Label number={31} />,
      });
  };

  const anchorTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const getCartProductsIds = () => {
    let res = [];
    for (const product of cart.products) {
      res.push(product.product_id);
    }
    return res;
  };

  const getAdditionalFieldLabel = (field) => {
    const translated = field?.translations?.[lang]?.name;
    return translated || field?.name || "";
  };

  const isAdditionalFieldMissing = (field, value) => {
    if (field.type == "bool") return value != 1;
    const text = value == null ? "" : String(value).trim();
    return text.length === 0;
  };

  const handleAdditionalFieldChange = (fieldId, value) => {
    setAdditionalFieldsValues((prev) => {
      const next = {
        ...prev,
        [fieldId]: value,
      };
      const field = Array.isArray(additionalCheckoutFields)
        ? additionalCheckoutFields.find((item) => item.id == fieldId)
        : null;
      if (field && field.mandatory == 1) {
        const missing = isAdditionalFieldMissing(field, next[fieldId]);
        setAdditionalFieldsErrors((prevErrors) => {
          if (missing) return prevErrors;
          if (!prevErrors[fieldId]) return prevErrors;
          const { [fieldId]: _removed, ...rest } = prevErrors;
          return rest;
        });
      }
      return next;
    });
  };

  const getMissingAdditionalFieldsErrors = () => {
    if (!Array.isArray(additionalCheckoutFields)) return {};
    const active = additionalCheckoutFields.filter((field) => field.active_selforder == 1);
    const mandatory = active.filter((field) => field.mandatory == 1);
    const errors = {};
    for (const field of mandatory) {
      const value = additionalFieldsValues[field.id];
      if (isAdditionalFieldMissing(field, value)) errors[field.id] = true;
    }
    return errors;
  };

  const handleCheckoutClick = () => {
    const errors = getMissingAdditionalFieldsErrors();
    const missingIds = Object.keys(errors);
    if (missingIds.length > 0) {
      setAdditionalFieldsErrors(errors);
      anchorTo(`acf-${missingIds[0]}`);
      return;
    }
    setAdditionalFieldsErrors({});
    handleCheckout();
  };

  const saveAdditionalCheckoutFields = (overrideValues = null) => {
    const payload = Object.keys(additionalFieldsValues).map((id) => ({
      id: parseInt(id),
      value: additionalFieldsValues[id],
    }));
    if (overrideValues) {
      for (const id of Object.keys(overrideValues)) {
        const intId = parseInt(id);
        const existing = payload.find((item) => item.id === intId);
        if (existing) existing.value = overrideValues[id];
        else payload.push({ id: intId, value: overrideValues[id] });
      }
    }
    const lastSaved = lastSavedAdditionalFieldsRef.current || {};
    if (payload.length === Object.keys(lastSaved).length) {
      let changed = false;
      for (const item of payload) {
        if (lastSaved[item.id] != item.value) {
          changed = true;
          break;
        }
      }
      if (!changed) return;
    }
    if (monoOrder) {
      if (editCart) editCart({ additional_checkout_fields: payload });
    } else {
      if (editTable) editTable({ additional_checkout_fields: payload });
    }
  };

  const saveAdditionalFieldOnBlur = (fieldId) => {
    const value = additionalFieldsValues[fieldId];
    saveAdditionalCheckoutFields({ [fieldId]: value });
  };

  const activeAdditionalCheckoutFields = Array.isArray(additionalCheckoutFields)
    ? additionalCheckoutFields
        .filter((field) => field.active_selforder == 1)
        .sort((a, b) => (a.position || 0) - (b.position || 0))
    : [];

  return (
    <>
      <div className="popup">
        {cart && (
          <div className="content cart">
            <div className="cart-header">
              <CloseButton onClick={() => setCartVisible(false)} />
              <h3 className="mb-0">
                <Label number={19} />
              </h3>
              <p className="mb-0 small">
                <Label number={73} />
              </p>
              {cart.allyoucaneat == 1 && cart.rounds?.max > 0 && (
                <div className="cart-rounds">
                  <Label number={192} /> {Math.min(cart.rounds.done + 1, cart.rounds.max)}/{cart.rounds.max}
                </div>
              )}

              {settings.selfordering.alert_limit != "" &&
                countProducts() >= parseInt(settings.selfordering.alert_limit) && (
                  <div className="alert p-2 alert-warning mt-1 mb-0 small">
                    <Label number={109} />
                  </div>
                )}
              <div className="header-buttons">
                {user && (
                  <button onClick={() => anchorTo("promo")}>
                    <Label number={119} />
                  </button>
                )}
                {user && settings?.credit?.active == 1 && cart.customer?.credit > 0 && (
                  <button className="cart-button" onClick={() => anchorTo("credit")}>
                    <Label number={90} />
                  </button>
                )}
                {settings.selfordering.hide_order_notes != 1 && (
                  <button onClick={() => setNotesPopup(true)}>
                    <Label number={4} />
                  </button>
                )}
              </div>
            </div>

            <div className="cart-scroll">
              <div className="cart-products">
                <Products
                  products={visibleCart.products}
                  timerActive={timerActive}
                  updateQty={updateQty}
                  mode={1}
                  groupByCustomer={
                    settings.selfordering.enable_group_by_customer == 1 && !singleOrderMode && !monoOrder
                  }
                />
                {cart.discounts_fidelity &&
                  cart.discounts_fidelity.map((discount, i) => (
                    <div key={"dis-" + i} className="cart-row">
                      <div className="name">
                        <button onClick={deleteDiscountFidelity} className="me-2 delete-button">
                          &times;
                        </button>
                        {discount.name}
                      </div>
                      <div className="prices">
                        <div className="price">
                          -{currencyFormat(parseFloat(discount.price), false, shop.currency)}
                        </div>
                      </div>
                    </div>
                  ))}
                {cart.discounts_code &&
                  cart.discounts_code.map((discount, i) => (
                    <div key={"disc-" + i} className="cart-row">
                      <div className="name">
                        {discount.removable == 1 && (
                          <button
                            onClick={() => deleteDiscountCode(discount.item_id)}
                            className="me-3 delete-button"
                          >
                            &times;
                          </button>
                        )}
                        {discount.name}
                      </div>
                      <div className="prices">
                        <div className="price">
                          -{currencyFormat(parseFloat(discount.price), false, shop.currency)}
                        </div>
                      </div>
                    </div>
                  ))}
                {credit && cart.credit_total > 0 && (
                  <div className="cart-row">
                    <div className="name">Credito prepagato</div>
                    <div className="prices">
                      <div className="price">
                        -{currencyFormat(parseFloat(cart.credit_total), false, shop.currency)}
                      </div>
                    </div>
                  </div>
                )}
                <div className="total-row">
                  <Label number={25} />: <span>{currencyFormat(visibleCart.total, false, shop.currency)}</span>
                </div>

              </div>
              {cart.notes && cart.notes[userID] && (
                <div className="cart-products">
                  <label>
                    <div className="title">
                      <Label number={4} />:
                    </div>
                  </label>
                  <div className="small">{cart.notes[userID]}</div>
                </div>
              )}
              {activeAdditionalCheckoutFields.length > 0 && (
                <div className="promo-box additional-checkout-box">
                  <div className="title-section"><Label number={151} /></div>
                  <div className="additional-checkout-fields" id="additional-checkout-fields">
                    {activeAdditionalCheckoutFields.map((field) => (
                        <div
                          className={`additional-checkout-field${field.type == "bool" ? " additional-checkout-field--checkbox" : ""
                            }`}
                          key={`acf-${field.id}`}
                        >
                          {field.type == "bool" ? (
                            <>
                              <input
                                id={`acf-${field.id}`}
                                type="checkbox"
                                className="form-check-input"
                                checked={additionalFieldsValues[field.id] == 1}
                                onChange={(e) => {
                                  const nextValue = e.target.checked ? 1 : 0;
                                  handleAdditionalFieldChange(field.id, nextValue);
                                  saveAdditionalCheckoutFields({ [field.id]: nextValue });
                                }}
                              />
                              <label htmlFor={`acf-${field.id}`}>
                                <div className="title">
                                  {getAdditionalFieldLabel(field)}
                                  {field.mandatory == 1 ? (
                                    <span className="required-asterisk"> *</span>
                                  ) : (
                                    ""
                                  )}
                                </div>
                              </label>
                              {additionalFieldsErrors[field.id] && (
                                <div className="additional-checkout-field-error">
                                  <Label number={150} />
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <label htmlFor={`acf-${field.id}`}>
                                <div className="title">
                                  {getAdditionalFieldLabel(field)}
                                  {field.mandatory == 1 ? (
                                    <span className="required-asterisk"> *</span>
                                  ) : (
                                    ""
                                  )}
                                </div>
                              </label>
                              {field.type == "textarea" ? (
                                <textarea
                                  id={`acf-${field.id}`}
                                  className="form-control"
                                  rows={3}
                                  value={additionalFieldsValues[field.id] || ""}
                                  onInput={(e) => {
                                    handleAdditionalFieldChange(field.id, e.target.value);
                                  }}
                                  onBlur={() => saveAdditionalFieldOnBlur(field.id)}
                                />
                              ) : (
                                <input
                                  id={`acf-${field.id}`}
                                  type="text"
                                  className="form-control"
                                  value={additionalFieldsValues[field.id] || ""}
                                  onInput={(e) => {
                                    handleAdditionalFieldChange(field.id, e.target.value);
                                  }}
                                  onBlur={() => saveAdditionalFieldOnBlur(field.id)}
                                />
                              )}
                              {additionalFieldsErrors[field.id] && (
                                <div className="additional-checkout-field-error">
                                  <Label number={150} />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {user && (
                <div className="promo-box" id="promo">
                  <label>
                    <div className="title">
                      <Label number={14} />:
                    </div>
                  </label>
                  <div className="button-groups">
                    <input id="code" type="text" className="form-control" />
                    <button onClick={addDiscountCode}>
                      <Label number={15} />
                    </button>
                  </div>
                </div>
              )}
              {settings.fidelity?.active == 1 &&
                user &&
                cart.availables_discounts_fidelity.length > 0 &&
                cart.discounts_fidelity.length == 0 && (
                  <div className="promo-box" id="fidelity">
                    <label>
                      <div className="title">
                        <Label number={84} />:
                      </div>
                      <div className="small">
                        <Label number={85} />: {cart.customer.points}{" "}
                      </div>
                    </label>
                    <div>
                      <select className="form-select" onChange={addDiscountFidelity}>
                        <option key={-1} value=""></option>
                        {cart.availables_discounts_fidelity.map((discount, i) => (
                          <option key={i} value={discount.id}>
                            {discount.name}
                          </option>
                        ))}
                      </select>
                      <div className="small">
                        <Label number={86} />: {cart.fidelity_points} <Label number={87} />
                      </div>
                    </div>
                  </div>
                )}
              {user && settings?.credit?.active == 1 && cart.customer?.credit > 0 && (
                <div className="promo-box" id="credit">
                  <label>
                    <div className="title">
                      <Label number={88} />:
                    </div>
                    <div className="small">
                      <Label number={89} />: {currencyFormat(cart.customer.credit, false, shop.currency)}
                    </div>
                  </label>
                  <div>
                    {credit ? (
                      <button className="cart-button" onClick={() => setCredit(false)}>
                        <Label number={91} />
                      </button>
                    ) : (
                      <button className="cart-button" onClick={() => setCredit(true)}>
                        <Label number={90} />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="totals">
              <div className="disclaimers">
                {user &&
                  legal &&
                  Object.keys(legal).map((id, i) => {
                    if (id == "newsletter") {
                      if (cart.customer && cart.customer[id] == 0) {
                        return (
                          <div className="form-group form-check" key={i}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={id}
                              name={id}
                              onChange={() => setNewsletter(newsletter == 1 ? 0 : 1)}
                              checked={newsletter}
                            />
                            <label className="form-check-label small" htmlFor={id}>
                              {legal[id].label_text}
                              <a href="#" onClick={() => setLegalPupop(legal[id])}>
                                {legal[id].label_linked}
                              </a>
                            </label>
                          </div>
                        );
                      }
                    }
                    if (id == "marketing") {
                      if (cart.customer && cart.customer[id] == 0) {
                        return (
                          <div className="form-group form-check" key={i}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={id}
                              name={id}
                              onChange={() => setMarketing(marketing == 1 ? 0 : 1)}
                              checked={marketing}
                            />
                            <label className="form-check-label small" htmlFor={id}>
                              {legal[id].label_text}
                              <a href="#" onClick={() => setLegalPupop(legal[id])}>
                                {legal[id].label_linked}
                              </a>
                            </label>
                          </div>
                        );
                      }
                    }
                  })}
                {legal &&
                  Object.keys(legal).map((id, i) => {
                    if (id == "privacy") {
                      return (
                        <div className="" key={i}>
                          {legal[id].label_text}
                          {settings?.orders?.privacy_page_id == "" ? (
                            <a href="#" onClick={() => setLegalPupop(legal[id])}>
                              {legal[id].label_linked}
                            </a>
                          ) : (
                            <a href="#" onClick={() => setPagePopup(settings.orders.privacy_page_id)}>
                              {legal[id].label_linked}
                            </a>
                          )}
                          {legal[id].label_text_after}
                        </div>
                      );
                    }
                  })}
                <div className="">
                  <Label number={106} />{" "}
                  <a href="#" onClick={() => setPagePopup(settings.orders.tos_page_id)}>
                    <Label number={107} />
                  </a>
                </div>
              </div>
              <div className="buttons">
                <button onClick={() => setCartVisible(false)}>
                  <Label number={6} />
                </button>
                {cart.rounds?.exhausted && cart.products.some((p) => p.price == 0 && p.exclude_from_ayce != 1 && !p.is_cover && !p.is_service) ? (
                  <button className="button-primary" disabled>
                    <Label number={191} />
                  </button>
                ) : timerActive && !cart.products.some((p) => p.exclude_from_ayce == 1 || (settings.selfordering?.disable_timer_for_priced == 1 && p.price > 0)) ? (
                  <button className="button-primary">{timerActive}</button>
                ) : (
                  <>
                    {cart.products.length > 0 ? (
                      <button className="button-primary" onClick={handleCheckoutClick}>
                        <Label number={21} />
                      </button>
                    ) : (
                      <button className="button-primary">
                        <Label number={21} />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {notesPopup && (
        <NotesPopup
          value={cart.notes ? cart.notes[userID] : ""}
          onChange={(notes) => saveNotes({ user_id: userID, notes: notes })}
          onClose={() => setNotesPopup(false)}
        />
      )}
      {productNotesPopup !== false && (
        <ProductNotesPopup itemId={productNotesPopup} onClose={() => setProductNotesPopup(false)} />
      )}
      {showPaymentPopup && (
        <PaymentPopup
          showPaymentPopup={showPaymentPopup}
          setShowPaymentPopup={setShowPaymentPopup}
          onPayment={checkout}
          closeCart={closeCart}
          checkoutData={{
            shop_id: shop.id,
            mono_order: monoOrder,
            customer_id: user.id,
            brand_id: brand ? brand.id : null,
            user_id: userID,
            timer_active: timerActive ? true : false,
            self_mode: accessType,
            credit: credit,
            customer: {
              id: user.id,
              name: user.name,
              surname: user.surname,
              email: user.email,
              mobile: user.mobile,
            },
          }}
        />
      )}
      {showYellgoPayPopup && <YellgoPayPopup />}
    </>
  );
}

export default Cart;
